import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { trackMixpanelEvent } from 'utils/analytics';
import * as yup from 'yup';

import {
  useSendEmailVerification,
  useVerifyEmail,
} from '@/modules/payment/apiHooks';
import { Button, Typography } from '@/shared/components/atoms';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

interface IFormValues {
  pin: string;
}
const VerifyEmail: React.FC<{ title?: string }> = ({
  title = 'email-verification',
}) => {
  const { user } = useAuth();
  const { closeModal } = useModal();

  const { t } = useTranslation('payment');
  const [countdown, setCountdown] = useState(0);
  const { mutate: sendEmail, isLoading: isSendEmailLoading } =
    useSendEmailVerification();
  const { mutate: verifyEmail, isLoading: isVerifyEmailLoading } =
    useVerifyEmail();

  const schema: yup.SchemaOf<IFormValues> = yup.object().shape({
    pin: yup.string().required(t('this-field-is-required')),
  });

  const methods = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      pin: '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = methods;

  const pin = watch('pin');

  const onSubmit = () => {
    verifyEmail(pin, {
      onSuccess: () => {
        closeModal();
        toast.success(t('email-verified-successfully'));
      },
    });
  };

  const sendEmailHandler = () => {
    if (!user?.email) return;
    setCountdown(15);
    trackMixpanelEvent('Verify Email Button', user);
    sendEmail(user.email);
  };

  useEffect(() => {
    sendEmailHandler();
    const timer = setInterval(() => {
      setCountdown((prevCountdown) =>
        prevCountdown > 0 ? prevCountdown - 1 : 0
      );
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex w-full flex-col gap-24 tablet:min-w-[420px]'
      >
        <div className='flex flex-col'>
          <Typography
            variant='h2'
            weight='semiBold'
            className='text-text-default'
          >
            {t(title)}
          </Typography>
          <Typography variant='body2' className='text-surface-10'>
            {t('verification-sent-email')} {user?.email}
          </Typography>
        </div>
        <div className='flex flex-col gap-16'>
          <Controller
            name='pin'
            control={control}
            render={({ field }) => (
              <OtpInput
                inputStyle='otp-input'
                containerStyle='otp-container'
                isInputNum
                numInputs={4}
                {...field}
                aria-invalid={errors?.pin ? 'true' : 'false'}
              />
            )}
          />
          {countdown > 0 ? (
            <Typography
              variant='caption'
              weight='medium'
              className='w-fit rounded-xl bg-outline-default px-16 py-2 text-primary-default'
            >
              {t('you-can-resubmit-the-code-within')}{' '}
              <span className='font-regular text-text-default'>
                0:{countdown}
              </span>
            </Typography>
          ) : (
            <Typography
              variant='caption'
              className='w-fit cursor-pointer rounded-xl bg-outline-default px-16 py-2 text-primary-default'
              onClick={sendEmailHandler}
            >
              {t('Resend_the _code', {
                ns: 'login',
              })}
            </Typography>
          )}
        </div>
        <Button
          size='full'
          type='submit'
          className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
          disabled={
            isVerifyEmailLoading || isSendEmailLoading || pin.length !== 4
          }
        >
          {t('verify')}
        </Button>
      </form>
    </FormProvider>
  );
};

export { VerifyEmail };
